import React from 'react';
import { ApolloProvider } from '@apollo/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import { createStore } from 'redux';
import { IntlProvider } from 'react-intl';
import { SnackbarProvider } from 'notistack';
import { createGenerateClassName, jssPreset, StylesProvider } from '@material-ui/core/styles';
import { create } from 'jss';
import { MuiThemeProvider, createMuiTheme, CssBaseline } from '@material-ui/core';
import JssProvider from 'react-jss/lib/JssProvider';
import SuccessionDealsWrapper from './SuccessionDealsWrapper';
import SuccessionDrawerWithListener from '../SuccessionDrawerWithListener';
import SuccessionThemeProvider from '../../../components/Succession/SuccessionThemeProvider';
import client from '../../../lib/apolloClient';
import {
  SUCCESSION_DRAWER_OPEN,
  SUCCESSION_DRAWER_CLOSE,
  SUCCESSION_PROFILE_DRAWER_UPDATE
} from '../../../constants/successionDrawerConstants';
import getMessages from '../../../lib/i18n/getMessages';
import { CurrencyProvider } from '../../../hooks/useCurrency';

// Create JSS instance with preset
const jss = create({
  ...jssPreset(),
});

// Create JSS class name generator
const generateClassName = createGenerateClassName({
  dangerouslyUseGlobalCSS: true,
  productionPrefix: 'fbSucc',
});

// Default theme
const defaultTheme = {
  palette: {
    primary: {
      main: '#3f51b5',
    },
    secondary: {
      main: '#f50057',
    },
  },
};

// Simple reducer for succession drawer state that matches the format in successionDrawerReducer.js
const initialState = {
  successionDrawer: {
    userName: null,
    profileData: {}
  }
};

const rootReducer = (state = initialState, action) => {
  switch (action.type) {
    case SUCCESSION_DRAWER_OPEN:
      return {
        ...state,
        successionDrawer: {
          ...state.successionDrawer,
          userName: action.userName
        }
      };
    case SUCCESSION_DRAWER_CLOSE:
      return {
        ...state,
        successionDrawer: {
          ...state.successionDrawer,
          userName: null,
          profileData: {}
        }
      };
    case SUCCESSION_PROFILE_DRAWER_UPDATE:
      return {
        ...state,
        successionDrawer: {
          ...state.successionDrawer,
          profileData: action.selectedProfileDrawer
        }
      };
    default:
      return state;
  }
};

// Create a Redux store with the simple reducer
const store = createStore(rootReducer);

// This container provides the Apollo client context 
// and router context needed for the SuccessionDealsWrapper component
const SuccessionDealsContainer = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  window.currency = (railsContext && railsContext.currency) || window.currency || 'USD';
  // Extract dealId and section from the URL if not provided in props
  const extractUrlParams = () => {
    const path = window.location.pathname;
    const pathParts = path.split('/');
    
    // Check if we have a dealId in the URL
    let urlDealId = null;
    let urlSection = null;
    
    if (pathParts.length >= 4 && pathParts[1] === 'succession' && pathParts[2] === 'deals') {
      urlDealId = pathParts[3];
      if (pathParts.length >= 5) {
        urlSection = pathParts[4];
      }
    }
    
    return {
      urlDealId,
      urlSection
    };
  };
  
  const { urlDealId, urlSection } = extractUrlParams();
  const dealId = props.dealId || urlDealId;
  const section = props.section || urlSection;
  
  // Get locale from window
  const locale = window.locale;
  
  // Make sure all required providers are present
  // Provider (Redux) -> Router -> IntlProvider -> JssProvider -> MaterialUI -> Snackbar -> Apollo
  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || window.currency || 'USD'}>
      <Provider store={store}>
        <Router>
          <IntlProvider locale={locale} messages={getMessages(locale)}>
            <JssProvider jss={jss} generateClassName={generateClassName} classNamePrefix="fbSucc-">
              <MuiThemeProvider theme={createMuiTheme(defaultTheme)}>
                <CssBaseline />
                <SnackbarProvider maxSnack={3}>
                  <ApolloProvider client={client}>
                    <SuccessionThemeProvider
                      company={props.company}
                      user={props.currentUser}
                    >
                      <SuccessionDealsWrapper
                        initialDealId={dealId}
                        initialSection={section}
                        company={props.company}
                        userType={props.userType}
                        currentUser={props.currentUser}
                      />
                      <SuccessionDrawerWithListener />
                    </SuccessionThemeProvider>
                  </ApolloProvider>
                </SnackbarProvider>
              </MuiThemeProvider>
            </JssProvider>
          </IntlProvider>
        </Router>
      </Provider>
    </CurrencyProvider>
  );
};

export default SuccessionDealsContainer; 